import { ReactElement } from 'react';
import { BrandedStoreSlice } from '@adapters/prismic/types/brandedStorePage';
import { PageType } from '@adapters/prismic/types/slices';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import type { HitProps } from '@components/algolia/Hit';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';
import { CompareTablesById } from '@adapters/prismic/utils/mapCompareTableData';
import CategoryPageSliceHandler from '../CategoryPageSliceHandler';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import { sliceBleedAttributes } from '../sliceBleed';
import { renderedSliceTypeIndices } from '../sliceVisibility';
import { buildViewTracking } from '../viewComponentTracking';
import TrackedSliceSection from '../TrackedSliceSection';

import styles from './CategoryPageSliceZone.module.scss';

export interface BrandedStoreSliceZoneProps {
  slices?: BrandedStoreSlice[] | HomepageSlice[];
  categoryPageRecommendations?: AlgonomyPlacement[];
  isEnhancedPlp?: boolean;
  /** ED-345/ED-221/ED-230: SKU->product data resolved in getStaticProps for image_hotspot, image_product_strip and bundle_strip slices. */
  sliceProducts?: Record<string, HitProps>;
  slicePromotions?: Record<number, BundlePromotion>;
  compareTables?: CompareTablesById;
}

const CategoryPageSliceZone = ({
  slices,
  categoryPageRecommendations,
  sliceProducts,
  slicePromotions,
  compareTables,
}: BrandedStoreSliceZoneProps): ReactElement => {
  const typeIndices = renderedSliceTypeIndices(slices ?? [], {
    compareTables,
    sliceProducts,
  });
  const viewTracking = buildViewTracking(
    slices,
    'cip',
    categoryPageRecommendations,
    { compareTables, sliceProducts }
  );

  return (
    <div>
      {slices?.map((slice: BrandedStoreSlice | HomepageSlice, i: number) => {
        const sliceTypeIndex = typeIndices[i];
        return sliceTypeIndex !== null ? (
          <TrackedSliceSection
            key={`category-page-slice-${slice?.id}`}
            className={styles.slice}
            tracking={viewTracking[i]}
            data-slice-type={slice?.slice_type}
            {...sliceBleedAttributes(slice)}
          >
            <CategoryPageSliceHandler
              slice={slice}
              pageType={PageType.categoryInfoPage}
              sliceIndex={i}
              sliceTypeIndex={sliceTypeIndex}
              recommendations={categoryPageRecommendations}
              sliceProducts={sliceProducts}
              slicePromotions={slicePromotions}
              compareTables={compareTables}
            />
          </TrackedSliceSection>
        ) : null;
      })}
    </div>
  );
};

export default CategoryPageSliceZone;
